import {
  Box,
  Stack,
  Typography,
  Button,
  styled,
  Divider,
  type ButtonProps,
} from "@mui/material";
import React from "react";

type Props = {
  title: string;
  subtitle: string;
  actions: {
    variant: "outlined" | "contained";
    startIcon?: React.ReactNode;
    label: string;
    onClick: () => void;
  }[];
};

const SecondaryBtn = styled((props: ButtonProps) => (
  <Button {...props} variant="outlined" />
))(({ theme }) => ({
  minWidth: "auto",
  padding: "12px 16px",
  fontSize: "12px",
  fontWeight: 600,
  borderRadius: "8px",
  textTransform: "none",
  lineHeight: 1.5,
  borderColor: theme.palette.text.primary,
  color: theme.palette.text.primary,
  "&:hover": {
    borderColor: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
}));

const PrimaryBtn = styled((props: ButtonProps) => (
  <Button {...props} variant="contained" />
))(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: "12px 16px",
  fontSize: "12px",
  textTransform: "none",
  borderRadius: "8px",
  fontWeight: 600,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const Wrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "8px",
  justifyContent: "space-between",
  alignItems: "center",
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    alignItems: "end",
  },
}));

export default function SectionHeader({ title, subtitle, actions }: Props) {
  return (
    <>
      <Wrapper>
        <Stack direction="column" sx={{ lineHeight: "1" }} spacing="4px">
          <Typography fontWeight={600} fontSize={20} gutterBottom>
            {title}
          </Typography>
          <Typography>{subtitle}</Typography>
        </Stack>
        <Stack
          direction="row"
          spacing="12px"
          sx={{
            width: { xs: "100%", sm: "auto" },
            "& > *": { flex: { xs: 1, sm: "initial" } },
          }}
        >
          {actions.map((action, index) => {
            const Tag =
              action.variant === "contained" ? PrimaryBtn : SecondaryBtn;
            return (
              <Tag
                key={index}
                onClick={action.onClick}
                startIcon={action.startIcon}
              >
                {action.label}
              </Tag>
            );
          })}
        </Stack>
      </Wrapper>
      <Divider sx={{ marginBlock: "24px" }} />
    </>
  );
}
